import { useContext } from "react";

import { FilterableCardItemDataContext } from "src/uniform/contexts/filterable-card-item-data-context";

import { useLocale } from "@/libraries/web-framework";

import { useWAEContext } from "../../../utils/wae-context-provider";
import UniformCard, { type UniformCardProps } from "../../card/src/card";

export type FilterableCardProps = UniformCardProps;

const PATH_PREFIX = "$:";

const resolveValue = <T,>(
    raw: T | undefined,
    itemData: unknown
): T | undefined => {
    if (typeof raw !== "string") return raw;
    if (!raw.startsWith(PATH_PREFIX)) return raw;
    const path = raw.slice(PATH_PREFIX.length);
    return getByPath(itemData, path) as T | undefined;
};

const DEFAULT_LOCALE = "en";

const isAbsoluteUrl = (value: string): boolean =>
    /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(value);

const isAlreadyLocalized = (value: string, locale: string): boolean =>
    value === `/${locale}` || value.startsWith(`/${locale}/`);

const withLocalePrefix = (
    value: unknown,
    locale: string | undefined
): unknown => {
    if (typeof value !== "string" || value.length === 0) return value;
    if (!locale || locale === DEFAULT_LOCALE) return value;
    if (isAbsoluteUrl(value)) return value;
    if (isAlreadyLocalized(value, locale)) return value;
    const path = value.startsWith("/") ? value : `/${value}`;
    return `/${locale}${path}`;
};

const getByPath = (obj: unknown, path: string): unknown => {
    if (obj === null || obj === undefined) return undefined;
    return path
        .split(".")
        .reduce<unknown>(
            (acc, key) =>
                acc !== null && acc !== undefined && typeof acc === "object"
                    ? (acc as Record<string, unknown>)[key]
                    : undefined,
            obj
        );
};

const FilterableCard = (rawProps: FilterableCardProps): JSX.Element => {
    const itemData = useContext(FilterableCardItemDataContext);
    const intlLocale = useLocale();
    const { locale: contextLocale } = useWAEContext();
    const activeLocale = contextLocale ?? intlLocale;

    const resolved: UniformCardProps = {
        alignment: resolveValue(rawProps.alignment, itemData),
        orientation: resolveValue(rawProps.orientation, itemData),
        columns: resolveValue(rawProps.columns, itemData),
        background: resolveValue(rawProps.background, itemData),
        eyebrow: resolveValue(rawProps.eyebrow, itemData),
        logo: resolveValue(rawProps.logo, itemData),
        title: resolveValue(rawProps.title, itemData),
        titleAs: resolveValue(rawProps.titleAs, itemData),
        body: resolveValue(rawProps.body, itemData),
        richText: resolveValue(rawProps.richText, itemData),
        linkText: resolveValue(rawProps.linkText, itemData),
        linkUrl: withLocalePrefix(
            resolveValue(rawProps.linkUrl, itemData),
            activeLocale
        ) as string | undefined,
        imageUrl: resolveValue(rawProps.imageUrl, itemData),
        imageAlt: resolveValue(rawProps.imageAlt, itemData),
        iconUrl: resolveValue(rawProps.iconUrl, itemData),
        iconSize: resolveValue(rawProps.iconSize, itemData),
        iconPaddingIncluded: resolveValue(
            rawProps.iconPaddingIncluded,
            itemData
        ),
        spotIllustrationUrl: resolveValue(
            rawProps.spotIllustrationUrl,
            itemData
        ),
        spotIllustrationAlt: resolveValue(
            rawProps.spotIllustrationAlt,
            itemData
        ),
        spotIllustrationWidth: resolveValue(
            rawProps.spotIllustrationWidth,
            itemData
        )
    };

    return <UniformCard {...resolved} />;
};

export default FilterableCard;
